/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { hasValidRecAccount } from 'sarsaparilla/auth';

export type AuthScreen =
    'login' | 'createAccount' | 'requestPasswordReset' | 'mfaCode' | null;

export interface AuthModalState {
    screen: AuthScreen;
    isGuestAllowed: boolean;
}

const initialState: AuthModalState = {
    screen: null,
    isGuestAllowed: false,
};

export const authModalSlice = createSlice({
    name: 'authModal',
    initialState,
    reducers: {
        openLoginModal: (state) => {
            // Bail if the user is already logged in. localStorage `recaccount`
            // is the source of truth for "logged in", so there is no modal to
            // show. Consumers that instead gate on redux `auth.account` stay
            // consistent with this because `useUpdateReduxWithAccountData()`
            // (in ExternalAppBootstrap, mounted app-wide via
            // TopNavigationExternal) hydrates redux from localStorage at boot.
            // A bail while a caller still sees an empty redux `account` is
            // therefore a transient boot-timing edge, not a dead-end to fix here.
            if (hasValidRecAccount()) {
                return;
            }

            state.screen = 'login';
            state.isGuestAllowed = false;
        },
        openLoginWithGuestModal: (state) => {
            // Bail if already logged in — see the note on `openLoginModal`:
            // localStorage is the source of truth and redux `auth.account` is
            // hydrated from it at boot, so the two stay consistent.
            if (hasValidRecAccount()) {
                return;
            }

            state.screen = 'login';
            state.isGuestAllowed = true;
        },
        openForgotPasswordModal: (state) => {
            state.screen = 'requestPasswordReset';
        },
        closeAuthModal: (state) => {
            state.screen = null;
            state.isGuestAllowed = false;
        },
        setScreen: (state, action: PayloadAction<AuthScreen>) => {
            state.screen = action.payload;
        },
    },
});

export const {
    openLoginModal,
    openLoginWithGuestModal,
    openForgotPasswordModal,
    closeAuthModal,
    setScreen,
} = authModalSlice.actions;

export const authModalActions = {
    openLoginModal,
    openLoginWithGuestModal,
    closeAuthModal,
};

export const authModalReducer = authModalSlice.reducer;

// cSpell:ignore recaccount
